import { observable } from "@legendapp/state";
import {
  listCampaignsByAdvertisers,
  listCampaignsByAgencies,
  listCampaignsByOrganization,
} from "@/apis/clients/campaignsClient";
import {
  getMinSelectableDate,
  toISODate,
} from "@/layouts/AppLayout/dateRangePresets";
import { sortedIdKey, toErrorMessage } from "@/stores/storeHelpers";
import type { CampaignSummary } from "@/types/campaign";
import { compareNames } from "@/utils/sortOptions";

interface CampaignScope {
  organizationId: number;
  agencyIds: number[];
  advertiserIds: number[];
}

interface CampaignsState {
  campaigns: CampaignSummary[];
  isLoading: boolean;
  error: string | null;
  loadedForScopeKey: string | null;
}

const defaultValues: CampaignsState = {
  campaigns: [],
  isLoading: false,
  error: null,
  loadedForScopeKey: null,
};

export const CampaignsStore$ = observable(defaultValues);

// One scope field per query, whichever selected level is most specific, rather
// than combining organization, agency and advertiser conditions. (Non-scope
// conditions can still ride along: campaignsClient appends an end_date floor.)
const getScopeKey = ({
  organizationId,
  agencyIds,
  advertiserIds,
}: CampaignScope) => {
  if (advertiserIds.length > 0) {
    return `advertiser:${sortedIdKey(advertiserIds)}`;
  }
  if (agencyIds.length > 0) {
    return `agency:${sortedIdKey(agencyIds)}`;
  }
  return `organization:${organizationId}`;
};

// Newest flight first; campaigns starting on the same date fall back to A–Z.
const sortByStartDateThenName = (campaigns: CampaignSummary[]) =>
  [...campaigns].sort((campaignA, campaignB) => {
    const { start_date: startDateA } = campaignA.duration;
    const { start_date: startDateB } = campaignB.duration;
    return (
      new Date(startDateB).getTime() - new Date(startDateA).getTime() ||
      compareNames(campaignA.name, campaignB.name)
    );
  });

const fetchCampaignsForScope = async ({
  organizationId,
  agencyIds,
  advertiserIds,
}: CampaignScope) => {
  if (advertiserIds.length > 0) {
    return listCampaignsByAdvertisers(advertiserIds);
  }
  if (agencyIds.length > 0) {
    return listCampaignsByAgencies(agencyIds);
  }
  return listCampaignsByOrganization(organizationId);
};

// A scope change (e.g. selecting an agency) can arrive while a previous,
// slower fetch (e.g. the initial org-wide, multi-page load) is still in
// flight. Gating on `isLoading` would silently drop the newer request with
// no retry, leaving stale data on screen — so instead every call is allowed
// to start, and a request token ensures only the most recent one's result
// ever gets committed.
let latestRequestId = 0;

export const fetchCampaigns = async (scope: CampaignScope) => {
  // The list is filtered by a rolling end_date floor (see campaignsClient), so
  // a list loaded yesterday is not the list for the same scope today.
  const scopeKey = `${getScopeKey(scope)}|${toISODate(getMinSelectableDate(new Date()))}`;

  if (CampaignsStore$.loadedForScopeKey.get() === scopeKey) {
    return;
  }

  latestRequestId += 1;
  const requestId = latestRequestId;
  CampaignsStore$.assign({ isLoading: true, error: null });

  try {
    const campaigns = sortByStartDateThenName(
      await fetchCampaignsForScope(scope)
    );
    if (requestId !== latestRequestId) return;

    CampaignsStore$.assign({
      campaigns,
      loadedForScopeKey: scopeKey,
    });
  } catch (error) {
    if (requestId !== latestRequestId) return;
    CampaignsStore$.assign({
      campaigns: [],
      error: toErrorMessage(error),
      loadedForScopeKey: null,
    });
  } finally {
    if (requestId === latestRequestId) {
      CampaignsStore$.isLoading.set(false);
    }
  }
};

export const clearCampaigns = () => {
  // Increment the request token so any in-flight fetch is treated as superseded
  // and its result discarded when it arrives.
  latestRequestId += 1;
  CampaignsStore$.assign({
    campaigns: [],
    isLoading: false,
    loadedForScopeKey: null,
    error: null,
  });
};
